/**
 * §7.8 Supply Chain Monitor. Live SUPPLY_CATALOG + projectStockout
 * against a surge-demand profile.
 */

import { useMemo, useState } from 'react';
import { SUPPLY_CATALOG, projectStockout, SUPPLY_CHAIN_REFERENCES, type SupplyClass } from '@biomax/plugin-supply-chain';
import { Sparkline } from '../sparkline';

export function SupplyChainMonitor() {
  const supplies = Object.values(SUPPLY_CATALOG);
  const [classId, setClassId] = useState<SupplyClass>('ppe-n95');
  const [stockpile, setStockpile] = useState<number>(2_000_000);
  const [replenishment, setReplenishment] = useState<number>(50_000);
  const [populationPer100k, setPopulationPer100k] = useState<number>(3000); // ~300M region

  const spec = SUPPLY_CATALOG[classId]!;

  const projection = useMemo(() => {
    // Surge for 90d, then return to baseline for the remaining 30d.
    const days = 120;
    const dailyDemand: number[] = [];
    for (let t = 0; t < days; t++) {
      const surge = t < 90 ? spec.surgeBurnMultiplier : 1;
      dailyDemand.push(spec.baselineBurnPer100k * surge * populationPer100k);
    }
    return projectStockout({
      dailyDemand, currentStockpile: stockpile,
      replenishmentPerDay: replenishment,
      replenishmentLeadTimeDays: Math.min(45, spec.productionLeadTimeDays / 2),
    });
  }, [spec, stockpile, replenishment, populationPer100k]);

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.8</span>
        <h1 className="surface__title">Supply Chain Monitor</h1>
      </div>
      <div className="surface__sub">
        {supplies.length}-class catalog · per-class burn × surge × replenishment lead time
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Supply class</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Class</span>
            <select value={classId} onChange={e => setClassId(e.target.value as SupplyClass)}>
              {supplies.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Current stockpile (units)</span>
            <input type="number" value={stockpile}
              onChange={e => setStockpile(parseInt(e.target.value, 10) || 0)} />
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Replenishment / day (units)</span>
            <input type="number" value={replenishment}
              onChange={e => setReplenishment(parseInt(e.target.value, 10) || 0)} />
          </label>
        </div>
        <div className="simctl" style={{ marginTop: 12 }}>
          <label className="simctl__field">
            <span className="simctl__label">Population × 100k</span>
            <input type="number" value={populationPer100k}
              onChange={e => setPopulationPer100k(parseInt(e.target.value, 10) || 1)} />
          </label>
          <div className="simctl__field">
            <span className="simctl__label">Baseline burn</span>
            <div style={{ fontSize: 14, fontFamily: 'var(--bm-font-mono)' }}>
              {spec.baselineBurnPer100k}/100k/day
            </div>
          </div>
          <div className="simctl__field">
            <span className="simctl__label">Surge multiplier</span>
            <div style={{ fontSize: 14, fontFamily: 'var(--bm-font-mono)' }}>
              ×{spec.surgeBurnMultiplier}
            </div>
          </div>
        </div>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="kpi__label">Stockout day</div>
          <div className={`kpi__value ${projection.stockoutDay !== null ? 'kpi__value--danger' : 'kpi__value--accent'}`}>
            {projection.stockoutDay !== null ? `Day ${projection.stockoutDay}` : 'NOT REACHED'}
          </div>
          <div className="kpi__sub">surge active days 0-90</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Cumulative unmet</div>
          <div className="kpi__value">{Math.round(projection.cumulativeUnmet).toLocaleString()}</div>
          <div className="kpi__sub">units short</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Production lead time</div>
          <div className="kpi__value">{spec.productionLeadTimeDays}d</div>
          <div className="kpi__sub">raw material → distribution</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Mfr concentration</div>
          <div className="kpi__value">{(spec.manufacturerConcentration * 100).toFixed(0)}%</div>
          <div className="kpi__sub">0 = distributed · 1 = single-source</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Shelf life</div>
          <div className="kpi__value">{spec.shelfLifeDays === 0 ? '—' : `${(spec.shelfLifeDays / 365).toFixed(1)}y`}</div>
          <div className="kpi__sub">post-mfr</div>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Inventory trajectory</span>
          <span className="panel__meta">120 days · surge through day 90</span>
        </div>
        <Sparkline values={projection.inventory} label="Inventory (units)"
          stroke={projection.stockoutDay !== null ? 'var(--bm-danger)' : 'var(--bm-accent)'} />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Catalog · all supply classes</span>
        </div>
        <table className="dtable">
          <thead>
            <tr>
              <th>Class</th>
              <th className="num">Burn/100k</th>
              <th className="num">Surge ×</th>
              <th className="num">Lead time</th>
              <th className="num">Mfr conc</th>
            </tr>
          </thead>
          <tbody>
            {supplies.map(s => (
              <tr key={s.id}>
                <td>{s.label}</td>
                <td className="num">{s.baselineBurnPer100k}</td>
                <td className="num">×{s.surgeBurnMultiplier}</td>
                <td className="num">{s.productionLeadTimeDays}d</td>
                <td className="num">{(s.manufacturerConcentration * 100).toFixed(0)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Verified citations</span>
          <span className="panel__meta">{SUPPLY_CHAIN_REFERENCES.filter(r => r.pmid).length} PMIDs · engine math + allocation framework</span>
        </div>
        <table className="dtable">
          <thead><tr><th>ID</th><th>Paper</th><th>PMID</th><th>Backs</th></tr></thead>
          <tbody>
            {SUPPLY_CHAIN_REFERENCES.map(r => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td style={{ fontSize: 12 }}>{r.label}</td>
                <td className="num">{r.pmid ?? '—'}</td>
                <td style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>{r.appliesTo}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontSize: 11, color: 'var(--bm-text-muted)', padding: '8px 12px', lineHeight: 1.5 }}>
          Per-supply-class catalog entries (burn rates, surge multipliers, lead times)
          are sourced from industry reports + WHO/HHS gray literature, not peer-reviewed
          journals — those citations carry source-strings only.
        </div>
      </div>
    </div>
  );
}
